import { toast } from "react-toastify";

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_VIDEO_DURATION_SECONDS = 20;
const ERROR_MESSAGE =
  "You can't upload or record a video more than 20 seconds.";

// ─── Core helper ─────────────────────────────────────────────────────────────
/**
 * Reads the duration of a File or Blob by loading it into a temporary
 * <video> element and resolving once metadata is available.
 *
 * @param {File|Blob} fileOrBlob
 * @returns {Promise<number>} duration in seconds
 */
export const getVideoDuration = (fileOrBlob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(fileOrBlob);
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video metadata."));
    };

    video.src = url;
  });

// ─── Main validation function ─────────────────────────────────────────────────
/**
 * Validates that a video File or Blob is no longer than MAX_VIDEO_DURATION_SECONDS.
 * No file-size (MB) limit is enforced.
 *
 * Shows a toast error when the duration exceeds the limit.
 *
 * @param {File|Blob} fileOrBlob  — the video to validate
 * @returns {Promise<boolean>}    — true if valid, false if duration exceeded
 */
export const validateVideoDuration = async (fileOrBlob) => {
  if (!fileOrBlob) return true;

  try {
    const duration = await getVideoDuration(fileOrBlob);

    if (duration > MAX_VIDEO_DURATION_SECONDS) {
      toast.error(ERROR_MESSAGE);
      return false;
    }

    return true;
  } catch {
    // If metadata can't be read, allow the file through — server can validate further
    return true;
  }
};
